import type { SignatureField } from '@/modules/templates/components/review/SignatureFieldOverlay';
import type {
  ContractSignatory,
  ContractSignatureLocation,
} from '@/modules/org/contracts/types/contract-document.types';

/** Field-box colors per signatory index (mirrors SignatureFieldOverlay's palette). */
export const SIGNATORY_COLORS = [
  'hsl(var(--selise-blue))',
  'hsl(var(--poly-green))',
  'hsl(var(--mauveine))',
  'hsl(var(--destructive))',
];

export const colorForIndex = (i: number) => SIGNATORY_COLORS[i % SIGNATORY_COLORS.length];

// Default box sizes in PDF points (match SignatureFieldOverlay minimums).
// Exported so anchor-derived seeding produces boxes identical to the defaults.
export const SIG_W = 160;
export const SIG_H = 48;
export const DATE_W = 80;
export const DATE_H = 34;
export const GAP = 20;

export const sigFieldId = (index: number) => `sig-${index}`;
export const dateFieldId = (index: number) => `date-${index}`;

/** A default signature+date placement for a signatory with no stored location. */
export function defaultFieldPair(
  signatoryIndex: number,
  pageNumber: number,
  pageDims: { widthPt: number; heightPt: number }
): SignatureField[] {
  // Lower-left area, stacked slightly per signatory so multiple defaults don't fully overlap.
  const x = Math.max(24, pageDims.widthPt * 0.12);
  const y = Math.min(
    pageDims.heightPt - SIG_H - 40,
    pageDims.heightPt * 0.72 + (signatoryIndex % 3) * (SIG_H + 24)
  );
  return [
    {
      id: sigFieldId(signatoryIndex),
      type: 'signature',
      signatoryIndex,
      pageNumber,
      x,
      y,
      width: SIG_W,
      height: SIG_H,
    },
    {
      id: dateFieldId(signatoryIndex),
      type: 'date',
      signatoryIndex,
      pageNumber,
      x: x + SIG_W + GAP,
      y,
      width: DATE_W,
      height: DATE_H,
    },
  ];
}

/**
 * Build the overlay's field list from persisted signatories. Each signatory that has a
 * `signatureLocation` gets a signature box there + a date box (its `dateLocation` if present,
 * else placed just to the right). Signatories without a location contribute no fields until
 * the user places one.
 */
export function buildFieldsFromSignatories(signatories: ContractSignatory[]): SignatureField[] {
  const fields: SignatureField[] = [];
  signatories.forEach((s, index) => {
    const sig = s.signatureLocation;
    if (!sig) return;
    fields.push({
      id: sigFieldId(index),
      type: 'signature',
      signatoryIndex: index,
      pageNumber: sig.pageNumber || 1,
      x: sig.x,
      y: sig.y,
      width: sig.width,
      height: sig.height,
    });
    const date = s.dateLocation;
    fields.push({
      id: dateFieldId(index),
      type: 'date',
      signatoryIndex: index,
      pageNumber: (date?.pageNumber ?? sig.pageNumber) || 1,
      x: date?.x ?? sig.x + sig.width + GAP,
      y: date?.y ?? sig.y,
      width: date?.width ?? DATE_W,
      height: date?.height ?? DATE_H,
    });
  });
  return fields;
}

const toLocation = (f: SignatureField): ContractSignatureLocation => ({
  x: Math.round(f.x),
  y: Math.round(f.y),
  width: Math.round(f.width),
  height: Math.round(f.height),
  pageNumber: f.pageNumber,
});

/** Merge current field positions back into the signatory rows (signature + date per index). */
export function applyFieldsToSignatories(
  signatories: ContractSignatory[],
  fields: SignatureField[]
): ContractSignatory[] {
  return signatories.map((s, index) => {
    const sig = fields.find((f) => f.signatoryIndex === index && f.type === 'signature');
    const date = fields.find((f) => f.signatoryIndex === index && f.type === 'date');
    return {
      ...s,
      signatureLocation: sig ? toLocation(sig) : (s.signatureLocation ?? null),
      dateLocation: date ? toLocation(date) : (s.dateLocation ?? null),
    };
  });
}

/**
 * Write placements back onto the signatory rows, recording which coordinate
 * space each one is in.
 *
 * A box the person dragged is stored in the space of the preview they dragged
 * it in, and the review seeds from it again on reopen. A box that came from
 * anchor detection on the converted PDF is in a *different* space — the PDF
 * paginates independently of docx-preview — so it is stored for the signature
 * service but must never be replayed into the preview.
 *
 * `includeDetected` draws that line. At send time both kinds are written,
 * because both were actually sent. While editing, only the dragged ones are —
 * an untouched box is re-derived from its anchor on every render, so persisting
 * it would freeze a live value AND make a later send mistake it for intent.
 */
export function applyPlacements(
  signatories: ContractSignatory[],
  fields: SignatureField[],
  userPlacedFieldIds: ReadonlySet<string>,
  { includeDetected }: { includeDetected: boolean }
): ContractSignatory[] {
  return signatories.map((s, index) => {
    const isUser = userPlacedFieldIds.has(sigFieldId(index));
    if (!isUser && !includeDetected) return s;

    const sig = fields.find((f) => f.signatoryIndex === index && f.type === 'signature');
    const date = fields.find((f) => f.signatoryIndex === index && f.type === 'date');
    if (!sig && !date) return s;

    return {
      ...s,
      signatureLocation: sig ? toLocation(sig) : (s.signatureLocation ?? null),
      dateLocation: date ? toLocation(date) : (s.dateLocation ?? null),
      signatureLocationSource: isUser ? ('user' as const) : ('detected' as const),
    };
  });
}

/** True when every signatory has a placed signature field. */
export function allSignatoriesPlaced(
  signatories: ContractSignatory[],
  fields: SignatureField[]
): boolean {
  if (signatories.length === 0) return false;
  return signatories.every((_, index) =>
    fields.some((f) => f.signatoryIndex === index && f.type === 'signature')
  );
}
